/**
 * Pure display helpers for the Sources list polish (ADR-056).
 *
 * These derive a clinician-facing row **label** from the stored, serialized
 * `source_kind` + the encounter date, WITHOUT ever renaming the stored title.
 * The stored title is load-bearing — it is part of
 * `uq_documents_source_natural_key (patient_id, title, content_hash)`,
 * absorption's deterministic-title idempotent skip, and the
 * `classify_legacy_title` legacy backfill (ADR-046) — so the polish is a pure
 * READ of the title, never a rewrite (ADR-056).
 *
 * The clinically meaningful "when" (the encounter / administration date) lives
 * **only** inside the deterministic title's ` — {date}` suffix — the backend
 * `render.py` writes `"{kind label} — {date}"`. It is NOT `Document.created_at`,
 * which is the absorption timestamp (`func.now()`, roughly identical across a
 * bulk warehouse pull) — using `created_at` as the row date would show every
 * source in a pull on the same day. So we parse the encounter date out of the
 * title.
 *
 * **Fail-visible (ADR-056, a hard requirement):** a title that does not parse to
 * an ISO date returns `null` here; the caller must then show something HONEST
 * (the verbatim title / an explicit "date unavailable") — never a blank, never
 * a wrong date, and never a silent fall back to `created_at`. We deliberately
 * accepted the title-format fragility; it must degrade loudly, not silently.
 */
import {
  isAssessmentKind,
  sourceKindLabel,
  UNTYPED_SOURCE_KIND,
} from "../../utils/sourceKindLabels";
import type { BlueprintDocument } from "../../types";

/** Supported dash separators before date suffix: em-dash (" — "), en-dash (" – "), hyphen (" - "). */
const TITLE_SEPARATORS = [" — ", " \u2013 ", " - "];

/** A strict leading `YYYY-MM-DD`, the only date shape the deterministic titles emit. */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse the encounter date out of a deterministic source title's suffix.
 *
 * The parse is intentionally strict — only a leading ISO `YYYY-MM-DD` in the
 * date suffix after an em-dash (`" — "`), en-dash (`" – "`), or regular dash (`" - "`)
 * separator is accepted. Anything else (no separator, the
 * `"unknown date"` placeholder `render.py` emits for a missing date, a
 * free-text uploaded title) returns `null` so the caller can fail VISIBLY
 * rather than display a wrong or blank date (ADR-056).
 *
 * The date is built in LOCAL time from the parsed parts (not `new Date(iso)`,
 * which is UTC midnight and can render as the previous day in a west-of-UTC
 * timezone), and rejected if the parts don't round-trip (e.g. month 13).
 *
 * @param title - The stored source-document title (never mutated).
 * @returns The parsed encounter :class:`Date`, or `null` when the suffix is not
 *   a strict ISO date.
 */
export function parseEncounterDateFromTitle(title: string): Date | null {
  let bestIdx = -1;
  let sepLen = 0;
  for (const sep of TITLE_SEPARATORS) {
    const idx = title.lastIndexOf(sep);
    if (idx > bestIdx) {
      bestIdx = idx;
      sepLen = sep.length;
    }
  }
  if (bestIdx === -1) return null;
  const suffix = title.slice(bestIdx + sepLen).trim();
  const match = ISO_DATE_PREFIX.exec(suffix);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const roundTrips =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day);
  return roundTrips ? date : null;
}

/**
 * Format a :class:`Date` as a compact numeric date (e.g. "4/7/26"), so the row's
 * title has more horizontal room (BLA-333). The locale is pinned EXPLICITLY to
 * `"en-US"` rather than relying on the browser's locale, matching this module's
 * existing fail-visible-not-locale-dependent posture.
 *
 * @param date - The date to format.
 * @returns The short numeric date string.
 */
export function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "2-digit",
  });
}

/** Milliseconds in one day, for whole-day recency math. */
const MS_PER_DAY = 86_400_000;
/** Beyond this many days, a "most recent" item is labelled generically, not relatively. */
const RECENCY_WEEKS_LIMIT_DAYS = 56;

/** Midnight (local) of the given date, so recency counts whole calendar days. */
function startOfDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

/**
 * A short recency label for the most-recent item in a group (D5).
 *
 * Relative language for recent encounters ("Today" / "Yesterday" / "N days ago"
 * / "N weeks ago"); a generic "Latest" for an undated or long-past most-recent
 * item (so the newest row is always marked, even when it is old). Non-most-recent
 * rows never call this — they show their absolute date instead.
 *
 * @param date - The encounter date, or `null` when it could not be parsed.
 * @param now - The current time (injected for deterministic tests).
 * @returns The recency label.
 */
export function recencyLabel(date: Date | null, now: Date): string {
  if (date === null) return "Latest";
  const days = Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY);
  if (days < 0) return "Latest";
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < RECENCY_WEEKS_LIMIT_DAYS) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  return "Latest";
}

/**
 * Whether a source has a known, non-fallback kind whose title is deterministic.
 *
 * `true` for the registered clinical kinds (screeners, visit notes, Vanderbilt,
 * BASC-3, and any future registered kind) whose title `render.py` writes as
 * `"{label} — {date}"`; `false` for the `unknown` fallback and for a manually
 * uploaded source (no persisted kind) — those have no deterministic title, so
 * the caller keeps their verbatim title rather than parsing a date out of it.
 *
 * @param kind - The document's `source_kind`.
 * @returns `true` when the row date should be parsed from the title.
 */
export function isKindedSource(kind: string | null | undefined): boolean {
  return !!kind && kind !== "unknown" && kind !== UNTYPED_SOURCE_KIND;
}

/** An "Added {absorption date}" note for a source with no encounter date. */
function addedNote(createdAt: string | undefined): string {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? "" : `Added ${formatDate(date)}`;
}

/** The fully-decided text content of one source row (ADR-056 / decision D1). */
export interface SourceRowContent {
  /**
   * Left-side text: `""` for a clean kinded folder-child (the folder header is
   * the type, so the row is date-only), the human kind label for a clean kinded
   * plain row, or the verbatim stored title for an unknown/uploaded source or a
   * fail-visible unparseable kinded title.
   */
  label: string;
  /** Right-side encounter date (e.g. "Aug 28, 2026"), or `""` when there is none. */
  date: string;
  /** Muted secondary note ("Added {date}" or "Date unavailable"), or `""`. */
  note: string;
  /**
   * `true` when the source is kinded but its title did not parse to an encounter
   * date, so the row fell back to the verbatim title (fail-visible, ADR-056).
   */
  unparsed: boolean;
}

/**
 * Decide a source row's display text from `source_kind` + the parsed encounter
 * date, never renaming the stored title (ADR-056 / decision D1).
 *
 * - **Unknown / uploaded** (no deterministic title): keep the verbatim title and
 *   show an honest "Added {absorption date}" note — there is no encounter date
 *   to parse and no redundant type prefix to strip.
 * - **Kinded, parseable**: show the encounter date. In a single-kind folder (whose header is
 *   the type) the row is date-only; an assessment folder child (which contains mixed instruments)
 *   or a plain single-source row shows the clean kind label plus the date.
 * - **Kinded, unparseable (fail-visible)**: keep the verbatim title and mark it
 *   "Date unavailable" — never a blank, a wrong date, or a silent `created_at`.
 *
 * @param doc - The source document.
 * @param options - `inFolder` distinguishes a folder-child (date-only for single-kind folders)
 *   from a row that needs a label (mixed assessment folder or plain single-source row).
 * @returns The decided {@link SourceRowContent}.
 */
export function sourceRowContent(
  doc: BlueprintDocument,
  options: { inFolder: boolean },
): SourceRowContent {
  if (!isKindedSource(doc.source_kind)) {
    return {
      label: doc.title,
      date: "",
      note: addedNote(doc.created_at),
      unparsed: false,
    };
  }
  const encounter = parseEncounterDateFromTitle(doc.title);
  if (encounter === null) {
    return {
      label: doc.title,
      date: "",
      note: "Date unavailable",
      unparsed: true,
    };
  }
  const date = formatDate(encounter);
  return options.inFolder && !isAssessmentKind(doc.source_kind)
    ? { label: "", date, note: "", unparsed: false }
    : {
        label: sourceKindLabel(doc.source_kind),
        date,
        note: "",
        unparsed: false,
      };
}
